/**
 * Product Media Swiper Component
 * Replaces the custom SliderComponent with Swiper.js for better performance and features
 */

class ProductMediaSwiper extends HTMLElement {
  constructor() {
    super();
    
    this.swiper = null;
    this.mediaItems = [];
    this.currentSlide = 0;
    this.totalSlides = 0;
    this.isInitialized = false;
    
    // Bind methods
    this.handleResize = this.handleResize.bind(this);
    this.updateCounter = this.updateCounter.bind(this);
    this.handleSlideChange = this.handleSlideChange.bind(this);
    
    this.init();
  }
  
  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initializeSwiper());
    } else {
      this.initializeSwiper();
    }
  }
  
  initializeSwiper() {
    if (this.isInitialized) return;
    
    const mediaList = this.querySelector('.product__media-list');
    
    if (!mediaList) {
      return;
    }
    
    // Convert existing media list to Swiper structure
    this.convertToSwiperStructure(mediaList);
    
    // Get media items
    this.mediaItems = Array.from(mediaList.querySelectorAll('.swiper-slide'));
    this.totalSlides = this.mediaItems.length;
    
    if (this.totalSlides === 0) {
      return;
    }
    
    // Initialize Swiper
    this.initSwiper(mediaList);
    
    // Setup event listeners
    this.setupEventListeners();
    
    // Add user interaction to enable autoplay
    this.addUserInteractionForAutoplay();
    
    this.isInitialized = true;
  }
  
  convertToSwiperStructure(mediaList) {
    // Add swiper classes to the media list
    mediaList.classList.add('swiper');
    
    // Remove conflicting original slider classes
    mediaList.classList.remove('slider--mobile', 'slider--tablet', 'slider--desktop');
    
    // Create swiper wrapper if it doesn't exist
    let swiperWrapper = mediaList.querySelector('.swiper-wrapper');
    if (!swiperWrapper) {
      swiperWrapper = document.createElement('div');
      swiperWrapper.className = 'swiper-wrapper';
      
      // Move all media items to swiper wrapper and add swiper-slide class
      const mediaItems = Array.from(mediaList.children);
      mediaItems.forEach(item => {
        item.classList.add('swiper-slide');
        // Remove original slider slide class
        item.classList.remove('slider__slide');
        swiperWrapper.appendChild(item);
      });
      
      // Clear the media list and add the wrapper
      mediaList.innerHTML = '';
      mediaList.appendChild(swiperWrapper);
    }
  }
  
  initSwiper(container) {
    const isMobile = window.innerWidth <= 767;
    const isTablet = window.innerWidth <= 989;
    
    // Get navigation elements
    const prevButton = this.querySelector('.slider-button--prev');
    const nextButton = this.querySelector('.slider-button--next');
    const counterCurrent = this.querySelector('.slider-counter--current');
    const counterTotal = this.querySelector('.slider-counter--total');
    
    // Update counter total
    if (counterTotal) {
      counterTotal.textContent = this.totalSlides;
    }
    
    // Swiper configuration
    const swiperConfig = {
      // Basic settings
      slidesPerView: 1,
      spaceBetween: 2,
      loop: false,
      speed: 300,
      
      // Touch settings
      allowTouchMove: true,
      grabCursor: true,
      touchRatio: 1,
      touchAngle: 45,
      simulateTouch: true,
      shortSwipes: true,
      longSwipes: true,
      longSwipesRatio: 0.5,
      longSwipesMs: 300,
      followFinger: true,
      threshold: 5,
      loop: true,
      touchMoveStopPropagation: false,
      iOSEdgeSwipeDetection: true,
      iOSEdgeSwipeThreshold: 20,
      touchEventsTarget: 'container',
      
      // Navigation
      navigation: {
        nextEl: nextButton,
        prevEl: prevButton,
        disabledClass: 'slider-button--disabled',
      },
      
      // Responsive breakpoints
      breakpoints: {
        768: {
          allowTouchMove: false,
          grabCursor: false,
          simulateTouch: false,
        },
        990: {
          // Desktop behavior - could be grid or carousel based on CSS classes
          allowTouchMove: false,
          grabCursor: false,
          simulateTouch: false,
        }
      },
      
      // Events
      on: {
        init: () => {
          this.updateCounter();
          this.handleMediaChange();
          // Initialize all slides immediately for smooth experience
          this.initializeAllSlides();
        },
        slideChange: () => {
          this.handleSlideChange();
        },
        slideChangeTransitionEnd: () => {
          this.handleMediaChange();
        },
        resize: () => {
          this.handleResize();
        }
      }
    };
    
    // Initialize Swiper
    this.swiper = new Swiper(container, swiperConfig);
    
    // Handle desktop grid mode
    this.handleDesktopMode();
  }
  
  handleDesktopMode() {
    const isDesktop = window.innerWidth >= 768;
    const isMobileOnly = this.classList.contains('product-media-swiper--mobile-only');
    const mediaWrapper = this.closest('.product__media-wrapper');
    const isCarouselMode = mediaWrapper && mediaWrapper.classList.contains('product__media-wrapper--carousel');
    
    // If this is a mobile-only swiper in grid mode
    if (isMobileOnly) {
      if (isDesktop) {
        // Hide mobile swiper on desktop
        this.style.display = 'none';
      } else {
        // Show mobile swiper on mobile
        this.style.display = '';
        if (this.swiper) {
          this.swiper.enable();
          this.swiper.update();
        }
      }
    } else if (isDesktop && !isCarouselMode) {
      // Legacy grid mode handling (shouldn't happen with new template structure)
      // but keep for backwards compatibility
      this.createStandaloneGrid();
    } else {
      // Carousel/slider mode - show Swiper normally
      this.showSwiper();
    }
  }
  
  createStandaloneGrid() {
    // Hide the entire Swiper component
    this.style.display = 'none';
    
    // Get the media wrapper
    const mediaWrapper = this.closest('.product__media-wrapper');
    if (!mediaWrapper) return;
    
    // Check if standalone grid already exists
    let standaloneGrid = mediaWrapper.querySelector('.product__media-list--standalone');
    
    if (!standaloneGrid) {
      // Create standalone grid
      standaloneGrid = document.createElement('ul');
      standaloneGrid.className = 'product__media-list product__media-list--standalone';
      
      // Get original media items from the media wrapper, not from Swiper
      const mediaWrapper = this.closest('.product__media-wrapper');
      const originalMediaItems = mediaWrapper.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)');
      
      originalMediaItems.forEach((item, index) => {
        // Only include items that have actual media content
        if (item.querySelector('img, video, model-viewer')) {
          const clonedItem = item.cloneNode(true);
          // Remove Swiper-specific classes
          clonedItem.classList.remove('swiper-slide', 'swiper-slide-duplicate');
          
          // Handle deferred media elements (videos, 3D models, etc.)
          const deferredMediaElements = clonedItem.querySelectorAll('deferred-media');
          deferredMediaElements.forEach((deferred, deferredIndex) => {
            // Load the deferred content (this will create the actual video element)
            if (typeof deferred.loadContent === 'function') {
              deferred.loadContent();
            }
            
            // Set up click handler for the deferred media
            deferred.addEventListener('click', function() {
              const video = this.querySelector('video');
              if (video) {
                video.muted = !video.muted;
              }
            });
          });
          
          // Also handle any direct video elements that might exist
          const directVideos = clonedItem.querySelectorAll('video:not(deferred-media video)');
          directVideos.forEach((video, videoIndex) => {
            // Set video attributes
            video.autoplay = true;
            video.loop = true;
            video.muted = true; // Required for autoplay in most browsers
            video.playsInline = true; // Required for mobile autoplay
            
            // Don't play immediately - let intersection observer handle it when video comes into view
            
            // Add click handler for mute/unmute toggle
            video.addEventListener('click', function() {
              this.muted = !this.muted;
            });
          });
          
          standaloneGrid.appendChild(clonedItem);
        }
      });
      
      // Insert standalone grid after the Swiper component
      this.parentNode.insertBefore(standaloneGrid, this.nextSibling);
    }
    
    // Show standalone grid
    standaloneGrid.style.display = 'grid';
    
    // Try to play videos after a short delay to ensure they're in the DOM
    setTimeout(() => {
      const allVideos = standaloneGrid.querySelectorAll('video');
      
      // Check if we're on iOS Safari
      const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && /Safari/.test(navigator.userAgent) && !/CriOS|FxiOS|OPiOS|mercury/.test(navigator.userAgent);
      
      // Use Intersection Observer to play videos when they're visible
      const videoObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          const video = entry.target;
          if (entry.isIntersecting) {
            if (!isIOSSafari) {
              // Only try to autoplay on non-iOS Safari browsers
              video.play().catch(error => {
                // Silently handle autoplay errors
              });
            }
          } else {
            video.pause();
          }
        });
      }, { 
        rootMargin: '50px 0px 50px 0px', // Start autoplay when within 50px of viewport
        threshold: 0.1 
      });
      
      allVideos.forEach((video, index) => {
        // Set up intersection observer
        videoObserver.observe(video);
        
        // Check if video is already in view and play it immediately (except on iOS Safari)
        const rect = video.getBoundingClientRect();
        const isInView = rect.top < window.innerHeight + 50 && rect.bottom > -50;
        
        if (isInView && !isIOSSafari) {
          video.play().catch(error => {
            // Silently handle autoplay errors
          });
        }
      });
    }, 100);
    standaloneGrid.style.gridTemplateColumns = 'repeat(6, 1fr)';
    standaloneGrid.style.gap = '0.16666667rem';
    standaloneGrid.style.marginTop = '-0.1666666666666667rem';
    standaloneGrid.style.marginBottom = '-0.1666666666666667rem';
    standaloneGrid.style.overflow = 'visible';
    
    // Apply grid column rules to media items
    const mediaItems = standaloneGrid.querySelectorAll('.product__media-item');
    mediaItems.forEach((item, index) => {
      if (index === 0) {
        item.style.gridColumn = '1 / span 6';
      } else {
        item.style.gridColumn = 'auto / span 3';
      }
      item.style.width = 'auto';
      item.style.height = 'auto';
      item.style.display = 'block';
    });
    
    // Hide navigation buttons
    const navButtons = mediaWrapper.querySelectorAll('.slider-button');
    navButtons.forEach(btn => btn.style.display = 'none');
    
    // Hide counter
    const counter = mediaWrapper.querySelector('.slider-counter');
    if (counter) counter.style.display = 'none';
  }
  
  showSwiper() {
    // Show the Swiper component
    this.style.display = '';
    
    // Hide standalone grid if it exists
    const mediaWrapper = this.closest('.product__media-wrapper');
    if (mediaWrapper) {
      const standaloneGrid = mediaWrapper.querySelector('.product__media-list--standalone');
      if (standaloneGrid) {
        standaloneGrid.style.display = 'none';
      }
      
      // Show navigation buttons
      const navButtons = mediaWrapper.querySelectorAll('.slider-button');
      navButtons.forEach(btn => btn.style.display = 'flex');
      
      // Show counter
      const counter = mediaWrapper.querySelector('.slider-counter');
      if (counter) counter.style.display = 'flex';
    }
    
    // Enable Swiper
    if (this.swiper) {
      this.swiper.enable();
      this.swiper.allowTouchMove = window.innerWidth <= 989;
      this.swiper.allowSlideNext = true;
      this.swiper.allowSlidePrev = true;
    }
  }
  
  
  handleSlideChange() {
    if (this.swiper) {
      this.currentSlide = this.swiper.realIndex;
      this.updateCounter();
    }
  }
  
  updateCounter() {
    const counterCurrent = this.querySelector('.slider-counter--current');
    if (counterCurrent && this.swiper) {
      counterCurrent.textContent = this.swiper.realIndex + 1;
    }
  }
  
  handleMediaChange() {
    // Handle any media-specific changes (like video autoplay, etc.)
    const activeSlide = this.swiper?.slides[this.swiper?.activeIndex];
    if (activeSlide) {
      // Pause videos in inactive slides only
      const allSlides = this.swiper?.slides || [];
      allSlides.forEach((slide, slideIndex) => {
        if (slideIndex !== this.swiper?.activeIndex) {
          const videos = slide.querySelectorAll('video');
          videos.forEach(video => {
            video.pause();
          });
        }
      });
      
      // Ensure videos in active slide are playing (only if they're in view)
      const activeVideos = activeSlide.querySelectorAll('video');
      
      activeVideos.forEach((video, index) => {
        if (video.paused) {
          // Check if video is in viewport before playing
          const rect = video.getBoundingClientRect();
          const isInView = rect.top < window.innerHeight + 50 && rect.bottom > -50;
          
          if (isInView) {
            video.play().catch(error => {
              // Silently handle autoplay errors
            });
          }
        }
      });
    }
  }
  
  setupVideoForAutoplay(video, identifier) {
    // Set video attributes for autoplay (but don't play immediately)
    video.autoplay = true;
    video.loop = true;
    video.muted = true;
    video.playsInline = true;
    
    // Add event listeners to track video state
    video.addEventListener('canplay', () => {
      // Only play if video is in viewport (handled by intersection observer)
      // Don't play immediately here
    });
    
    // Force video to load
    if (video.src) {
      video.load();
    }
    
    // Don't try to play immediately - let intersection observer handle it
    // when video comes into view
    
    // Add click handler for mute/unmute toggle
    video.addEventListener('click', function() {
      this.muted = !this.muted;
    });
  }
  
  handleResize() {
    if (this.swiper) {
      // Debounce resize handling
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.swiper.update();
        this.handleDesktopMode();
      }, 150);
    }
  }
  
  
  addUserInteractionForAutoplay() {
    // Add a one-time click handler to enable autoplay
    const enableAutoplay = () => {
      this.userHasInteracted = true;
      
      // Try to play all videos now that user has interacted
      const allVideos = this.querySelectorAll('video');
      allVideos.forEach((video, index) => {
        if (video.paused) {
          video.play().then(() => {
            // Hide modal opener when video starts playing
            this.hideModalOpenerForVideo(video);
          }).catch(error => {
            // Silently handle autoplay errors
          });
        }
      });
      
      // Remove the event listeners
      this.removeEventListener('click', enableAutoplay);
      this.removeEventListener('touchstart', enableAutoplay);
    };
    
    // Add event listeners for user interaction
    this.addEventListener('click', enableAutoplay, { once: true });
    this.addEventListener('touchstart', enableAutoplay, { once: true });
  }
  
  hideModalOpenerForVideo(video) {
    // Find the modal opener (play button/poster) for this video
    const mediaItem = video.closest('.product__media-item');
    if (mediaItem) {
      const modalOpener = mediaItem.querySelector('.product__modal-opener');
      if (modalOpener) {
        modalOpener.style.display = 'none';
      }
      
      // Also hide any poster images
      const posterImg = mediaItem.querySelector('.product__modal-opener .product__media img');
      if (posterImg) {
        posterImg.style.opacity = '0';
      }
    }
  }
  
  initializeAllSlides() {
    // Get all slides
    const allSlides = this.swiper?.slides || [];
    
    allSlides.forEach((slide, slideIndex) => {
      // Load all deferred media in this slide
      const deferredMediaElements = slide.querySelectorAll('deferred-media');
      deferredMediaElements.forEach((deferred, deferredIndex) => {
        // Hide poster image immediately when deferred media loads
        const mediaItem = deferred.closest('.product__media-item');
        if (mediaItem) {
          // Add a class to mark this media item as having deferred media
          mediaItem.classList.add('has-deferred-media');
          
          // Hide all possible overlay elements immediately
          const modalOpener = mediaItem.querySelector('.product__modal-opener');
          if (modalOpener) {
            modalOpener.style.display = 'none';
          }
          
          const posterImg = mediaItem.querySelector('.product__modal-opener .product__media img');
          if (posterImg) {
            posterImg.style.opacity = '0';
          }
          
          // Hide any promotional overlays or placeholders
          const overlays = mediaItem.querySelectorAll('[class*="overlay"], [class*="promo"], [class*="banner"], [class*="placeholder"]');
          overlays.forEach(overlay => {
            overlay.style.display = 'none';
          });
          
          // Hide any elements with common promotional text
          const promoElements = mediaItem.querySelectorAll('*');
          promoElements.forEach(element => {
            const text = element.textContent?.toLowerCase() || '';
            if (text.includes('get 10% off') || text.includes('new arrival') || text.includes('sale') || text.includes('discount')) {
              element.style.display = 'none';
            }
          });
        }
        
        if (typeof deferred.loadContent === 'function') {
          deferred.loadContent();
        }
      });
      
      // Wait a bit for deferred media to load, then set up videos
      setTimeout(() => {
        const videos = slide.querySelectorAll('video');
        
        videos.forEach((video, videoIndex) => {
          const identifier = `slide-${slideIndex}-video-${videoIndex}`;
          this.setupVideoForAutoplay(video, identifier);
          
          // Don't play immediately - let intersection observer handle it when video comes into view
        });
      }, 100);
    });
  }
  
  setupEventListeners() {
    // Resize listener
    window.addEventListener('resize', this.handleResize);
    
    // Keyboard navigation
    this.addEventListener('keydown', (e) => {
      if (this.swiper) {
        switch(e.key) {
          case 'ArrowLeft':
            e.preventDefault();
            this.swiper.slidePrev();
            break;
          case 'ArrowRight':
            e.preventDefault();
            this.swiper.slideNext();
            break;
        }
      }
    });
    
    // Variant change listener
    document.addEventListener('variant:change', (e) => {
      this.handleVariantChange(e);
    });
    
    // Make container focusable for keyboard navigation
    this.setAttribute('tabindex', '0');
  }
  
  handleVariantChange(e) {
    if (!this.swiper) {
      return;
    }
    
    if (!e.detail || !e.detail.variant) {
      return;
    }
    
    const variant = e.detail.variant;
    
    // On mobile, scroll the gallery back into view
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      // Find the media wrapper or this element to scroll to
      const mediaWrapper = this.closest('.product__media-wrapper') || this;
      
      // Scroll with a smooth animation
      mediaWrapper.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
    
    // Find the media item that corresponds to this variant's featured media
    if (variant.featured_media) {
      // Get section ID and strip the 'shopify-section-' prefix if present
      let sectionId = this.closest('section')?.id || 'main';
      if (sectionId.startsWith('shopify-section-')) {
        sectionId = sectionId.replace('shopify-section-', '');
      }
      const mediaId = `${sectionId}-${variant.featured_media.id}`;
      
      const targetMedia = this.querySelector(`[data-media-id="${mediaId}"]`);
      
      if (targetMedia) {
        // Find the slide index for this media item
        const slides = this.swiper.slides;
        let targetIndex = -1;
        
        for (let i = 0; i < slides.length; i++) {
          const slide = slides[i];
          if (slide.contains(targetMedia)) {
            targetIndex = i;
            break;
          }
        }
        
        if (targetIndex >= 0) {
          this.swiper.slideTo(targetIndex, 300); // 300ms transition
        }
      }
    }
  }
  
  // Public methods for external control
  goToSlide(index) {
    if (this.swiper && index >= 0 && index < this.totalSlides) {
      this.swiper.slideTo(index);
    }
  }
  
  nextSlide() {
    if (this.swiper) {
      this.swiper.slideNext();
    }
  }
  
  prevSlide() {
    if (this.swiper) {
      this.swiper.slidePrev();
    }
  }
  
  // Cleanup
  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    
    window.removeEventListener('resize', this.handleResize);
    clearTimeout(this.resizeTimeout);
  }
}

// Register the custom element
customElements.define('product-media-swiper', ProductMediaSwiper);

/**
 * Initialize desktop grid gallery (for grid mode)
 * Handles video autoplay and deferred media in the desktop grid
 */
function initDesktopGridGallery() {
  const desktopGrid = document.querySelector('.product__media-list--desktop-grid');
  
  if (!desktopGrid) {
    return;
  }
  
  // Add variant change listener for desktop grid
  document.addEventListener('variant:change', (e) => {
    if (!e.detail || !e.detail.variant) return;
    
    // Ensure desktop grid is still visible after variant change
    if (window.innerWidth >= 768) {
      desktopGrid.style.display = 'grid';
      desktopGrid.style.visibility = 'visible';
      desktopGrid.style.opacity = '1';
      
      // Force show all media items and deferred media
      const mediaItems = desktopGrid.querySelectorAll('.product__media-item');
      const deferredMedia = desktopGrid.querySelectorAll('.deferred-media');
      const images = desktopGrid.querySelectorAll('img');
      
      mediaItems.forEach((item) => {
        item.style.display = 'block';
        item.style.visibility = 'visible';
        item.style.opacity = '1';
      });
      
      deferredMedia.forEach((deferred) => {
        deferred.style.display = 'block';
        deferred.style.visibility = 'visible';
        deferred.style.opacity = '1';
      });
      
      images.forEach((img) => {
        img.style.display = 'block';
        img.style.visibility = 'visible';
        img.style.opacity = '1';
      });
    }
  });
  
  // Force show desktop grid if on desktop
  if (window.innerWidth >= 768) {
    desktopGrid.style.display = 'grid';
    desktopGrid.style.visibility = 'visible';
    desktopGrid.style.opacity = '1';
    
    // Force show all media items and deferred media
    const mediaItems = desktopGrid.querySelectorAll('.product__media-item');
    const deferredMedia = desktopGrid.querySelectorAll('.deferred-media');
    const images = desktopGrid.querySelectorAll('img');
    
    mediaItems.forEach((item, index) => {
      item.style.display = 'block';
      item.style.visibility = 'visible';
      item.style.opacity = '1';
    });
    
    deferredMedia.forEach((deferred, index) => {
      deferred.style.display = 'block';
      deferred.style.visibility = 'visible';
      deferred.style.opacity = '1';
    });
    
    images.forEach((img, index) => {
      img.style.display = 'block';
      img.style.visibility = 'visible';
      img.style.opacity = '1';
    });
  }
  
  // Handle deferred media elements (videos)
  const deferredMediaElements = desktopGrid.querySelectorAll('deferred-media');
  deferredMediaElements.forEach((deferred, index) => {
    // Load the deferred content
    if (typeof deferred.loadContent === 'function') {
      deferred.loadContent();
    }
  });
  
  // Wait for deferred media to load, then set up videos
  setTimeout(() => {
    const videos = desktopGrid.querySelectorAll('video');
    
    // Check if we're on iOS Safari
    const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && /Safari/.test(navigator.userAgent) && !/CriOS|FxiOS|OPiOS|mercury/.test(navigator.userAgent);
    
    // Use Intersection Observer to play videos when they're visible
    const videoObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const video = entry.target;
        if (entry.isIntersecting) {
          if (!isIOSSafari) {
            // Only try to autoplay on non-iOS Safari browsers
            video.play().catch(error => {
              // Silently handle autoplay errors
            });
          }
        } else {
          video.pause();
        }
      });
    }, { 
      rootMargin: '50px 0px 50px 0px', // Start autoplay when within 50px of viewport
      threshold: 0.1 
    });
    
    videos.forEach((video, index) => {
      // Set video attributes for autoplay and iOS Safari compatibility
      video.autoplay = true;
      video.loop = true;
      video.muted = true;
      video.playsInline = true;
      video.setAttribute('webkit-playsinline', 'true');
      video.setAttribute('playsinline', 'true');
      video.setAttribute('preload', 'none');
      
      // Set up intersection observer
      videoObserver.observe(video);
      
      // Check if video is already in view and play it immediately (except on iOS Safari)
      const rect = video.getBoundingClientRect();
      const isInView = rect.top < window.innerHeight + 50 && rect.bottom > -50;
      
      if (isInView && !isIOSSafari) {
        video.play().catch(error => {
          // Silently handle autoplay errors
        });
      }
      
      // Add click handler for mute/unmute toggle
      video.addEventListener('click', function() {
        this.muted = !this.muted;
      });
    });
  }, 100);
}

// Initialize desktop grid when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDesktopGridGallery);
} else {
  initDesktopGridGallery();
}

// Handle resize to show/hide desktop grid appropriately
window.addEventListener('resize', function() {
  const desktopGrid = document.querySelector('.product__media-list--desktop-grid');
  if (!desktopGrid) return;
  
  const isDesktop = window.innerWidth >= 768;
  
  if (isDesktop) {
    desktopGrid.style.display = 'grid';
    desktopGrid.style.visibility = 'visible';
    desktopGrid.style.opacity = '1';
    
    // Force show all media items and deferred media
    const mediaItems = desktopGrid.querySelectorAll('.product__media-item');
    const deferredMedia = desktopGrid.querySelectorAll('.deferred-media');
    const images = desktopGrid.querySelectorAll('img');
    
    mediaItems.forEach((item) => {
      item.style.display = 'block';
      item.style.visibility = 'visible';
      item.style.opacity = '1';
    });
    
    deferredMedia.forEach((deferred) => {
      deferred.style.display = 'block';
      deferred.style.visibility = 'visible';
      deferred.style.opacity = '1';
    });
    
    images.forEach((img) => {
      img.style.display = 'block';
      img.style.visibility = 'visible';
      img.style.opacity = '1';
    });
  } else {
    desktopGrid.style.display = 'none';
  }
});
